/**
 * Push NATIVO (iOS/Android) — fachada única sobre o `@capacitor-firebase/messaging`.
 *
 * ── Por que uma FACHADA, e não o plugin circulando ──────────────────────────
 *
 * Lição do BUG-154, medida em device (builds 41/42): `registerPlugin` devolve
 * um Proxy que fabrica método para QUALQUER propriedade — `then` incluído.
 * Resolver uma promise com esse Proxy (inclusive retorná-lo de função async)
 * pendura o `await` para sempre, em silêncio. O plugin funciona perfeitamente
 * para CHAMAR método; o defeito é só ser tratado como valor de promise.
 * Por isso: o Proxy NUNCA atravessa fronteira de promise aqui — todo export
 * devolve valores planos (string, boolean, função de unsubscribe).
 *
 * ── Por que import ESTÁTICO ─────────────────────────────────────────────────
 *
 * Mesma decisão do `revenueCat.ts`, pela mesma medição: o `import()` dinâmico
 * pendurou para sempre no WKWebView nos builds 41/42. O wrapper JS do plugin é
 * pequeno e a avaliação na web é inerte — quem impede a web de FALAR com o
 * plugin é o guard de plataforma em cada função, não a ausência do módulo.
 *
 * ── Quem pode importar este módulo ──────────────────────────────────────────
 *
 * SÓ o `lib/firebase/messaging.ts`. Ele é o dono único do canal de push do
 * client (web + nativo); tela que falar com este módulo direto pula a decisão
 * de plataforma e reabre a doença do BUG-81 (duas fontes da mesma verdade).
 */

import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import type { PermissionState } from "@capacitor/core";
import { isNativeApp } from "../platform/native";

/**
 * O runtime nativo tem o plugin de messaging por trás da bridge?
 *
 * No Capacitor o bundle web viaja DENTRO do binário, então JS novo com nativo
 * velho é impossível por construção — mas a checagem é barata, é a resposta
 * honesta ("existe implementação registrada?"), e protege o único cenário em
 * que divergem: dev servindo bundle novo contra app antigo via live reload.
 */
export function isNativePushRuntime(): boolean {
  if (!isNativeApp()) return false;
  try {
    return Capacitor.isPluginAvailable("FirebaseMessaging");
  } catch {
    return false;
  }
}

/**
 * `PermissionState` do Capacitor → `NotificationPermission` da web.
 * `prompt`/`prompt-with-rationale` são os estados "ainda não perguntou", que
 * na gramática web é `default` — é o que `canOfferPushPrompt` e o toggle de
 * Ajustes já sabem ler.
 */
function toWebPermission(state: PermissionState): NotificationPermission {
  if (state === "granted") return "granted";
  if (state === "denied") return "denied";
  return "default";
}

/** Estado atual da permissão de notificação no sistema, SEM pedir nada. */
export async function nativeCheckPermission(): Promise<NotificationPermission> {
  if (!isNativePushRuntime()) return "denied";
  const status = await FirebaseMessaging.checkPermissions();
  return toWebPermission(status.receive);
}

/**
 * Pede a permissão ao sistema (diálogo nativo, one-shot por instalação no
 * iOS). Igual ao caminho web: se já foi decidida, o SO não reabre o diálogo —
 * a chamada só devolve o estado vigente.
 */
export async function nativeRequestPermission(): Promise<NotificationPermission> {
  if (!isNativePushRuntime()) return "denied";
  const status = await FirebaseMessaging.requestPermissions();
  return toWebPermission(status.receive);
}

/**
 * Token FCM deste device. No iOS o plugin espera o registro em APNs por
 * baixo; sem a capability de Push no build isto falha — e é exatamente a
 * falha que `NATIVE_PUSH_PROMPT_READY` existe para não deixar acontecer em
 * escala (ver pushPlatformGate.ts).
 *
 * Devolve `null` em qualquer falha: quem consome já trata token ausente como
 * "não registrou" (mesmo contrato do caminho web).
 */
export async function nativeGetToken(): Promise<string | null> {
  if (!isNativePushRuntime()) return null;
  try {
    const result = await FirebaseMessaging.getToken();
    return result.token || null;
  } catch (err) {
    console.warn("[Lura] FirebaseMessaging.getToken falhou:", err);
    return null;
  }
}

export interface NativePushMessage {
  title?: string;
  body?: string;
  type?: string;
  url?: string;
}

function payloadFromNotification(notification: {
  title?: string;
  body?: string;
  data?: unknown;
}): NativePushMessage {
  const data = (notification.data ?? {}) as Record<string, unknown>;
  return {
    title: notification.title,
    body: notification.body,
    type: typeof data.type === "string" ? data.type : undefined,
    url: typeof data.url === "string" ? data.url : undefined,
  };
}

/**
 * Mensagem chegando com o app em PRIMEIRO PLANO. O sistema também desenha o
 * banner (presentationOptions no capacitor.config.json), então isto é para a
 * UI reagir, não para renderizar a notificação.
 *
 * Devolve função de unsubscribe — plana, nunca o handle do plugin.
 */
export async function nativeOnForegroundMessage(
  cb: (payload: NativePushMessage) => void,
): Promise<() => void> {
  if (!isNativePushRuntime()) return () => undefined;
  const handle = await FirebaseMessaging.addListener("notificationReceived", (event) => {
    cb(payloadFromNotification(event.notification));
  });
  return () => {
    void handle.remove();
  };
}

/**
 * TOQUE na notificação (app em background, ou abrindo pelo toque). É o
 * equivalente nativo do roteamento por `data.url` que o service worker web já
 * faz — sem este listener, todo toque cai na tela em que o app estava, que é
 * a classe do BUG-152 (push que promete um destino e entrega outro).
 */
export async function nativeOnNotificationTap(
  cb: (payload: NativePushMessage) => void,
): Promise<() => void> {
  if (!isNativePushRuntime()) return () => undefined;
  const handle = await FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
    cb(payloadFromNotification(event.notification));
  });
  return () => {
    void handle.remove();
  };
}
